import { createFileRoute, notFound } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import {
  Star, FileText, MessageSquareQuote, ShoppingCart, Stethoscope, Banknote,
  TrendingUp, Zap, Target, Users, Award, ShieldCheck, Workflow, Globe,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type Block = { icon: LucideIcon; title: string; body: string };
type Project = { name: string; tag: string; result: string };
type ChildPage = {
  eyebrow: string;
  title: string;
  sub: string;
  metaDesc: string;
  intro: string;
  blocks: Block[];
  projects: Project[];
};

const CHILDREN: Record<string, ChildPage> = {
  "featured-projects": {
    eyebrow: "Portfolio · Work",
    title: "Featured Projects",
    sub: "A handful of launches we're most proud of.",
    metaDesc: "Featured CoderLity projects across SaaS, e-commerce, fintech and healthcare — outcomes, scope and stack.",
    intro:
      "These are the launches where everything came together — product, design, engineering and growth — to move the needle for our clients.",
    blocks: [
      { icon: Star, title: "Awwwards-grade craft", body: "Multiple SOTD and Honorable Mentions across client launches." },
      { icon: TrendingUp, title: "Real business impact", body: "Revenue lift, conversion gains and operational savings, not vanity metrics." },
      { icon: Zap, title: "Shipped fast", body: "Most featured projects went live within 8–14 weeks of kickoff." },
      { icon: Award, title: "Long-term partners", body: "Most featured clients are 2+ year partners — proof the work compounds." },
    ],
    projects: [
      { name: "Atlas SaaS", tag: "B2B SaaS", result: "+62% trial → paid conversion" },
      { name: "Noor Retail", tag: "E-commerce", result: "3.4× revenue in 12 months" },
      { name: "MediCare Connect", tag: "Healthcare", result: "120k patient records migrated, zero downtime" },
      { name: "Fluent Pay", tag: "Fintech", result: "SOC 2 + launch in 16 weeks" },
    ],
  },
  "case-studies": {
    eyebrow: "Portfolio · Work",
    title: "Case Studies",
    sub: "Deep-dives into the work, the trade-offs and the outcomes.",
    metaDesc: "In-depth CoderLity case studies — problem, approach, stack and measurable outcomes across industries.",
    intro:
      "Each case study walks through the business problem, the approach we took, the architecture and design choices we made, and the measurable outcomes after launch.",
    blocks: [
      { icon: Target, title: "Problem first", body: "We lead with the business problem, not the deliverables." },
      { icon: Workflow, title: "Honest trade-offs", body: "What we chose, what we deferred and why." },
      { icon: TrendingUp, title: "Measured outcomes", body: "Numbers tracked 90+ days post-launch with the client." },
      { icon: Users, title: "Client voices", body: "Quotes from founders, PMs and ops leads on the ground." },
    ],
    projects: [
      { name: "Atlas SaaS migration", tag: "SaaS · Infra", result: "Monolith → modular in 14 weeks" },
      { name: "Noor catalog rebuild", tag: "E-commerce · CRO", result: "+38% PDP add-to-cart" },
      { name: "MediCare telehealth", tag: "Healthcare · Mobile", result: "HIPAA launch in 90 days" },
      { name: "Fluent Pay onboarding", tag: "Fintech · KYC", result: "−54% drop-off in KYC" },
    ],
  },
  "client-testimonials": {
    eyebrow: "Portfolio · Work",
    title: "Client Testimonials",
    sub: "Quotes from the people we ship for.",
    metaDesc: "Client testimonials and quotes from CoderLity partners across SaaS, e-commerce, healthcare and fintech.",
    intro:
      "We let our partners do most of the talking. Here's what founders, product leaders and operators have said about working with our team.",
    blocks: [
      { icon: MessageSquareQuote, title: "Verified quotes", body: "Every testimonial is published with the client's permission." },
      { icon: Users, title: "Across roles", body: "Founders, PMs, designers, engineers, marketers." },
      { icon: Globe, title: "Across geos", body: "Saudi Arabia, UAE, UK, US, Germany, Singapore and more." },
      { icon: Award, title: "Long engagements", body: "Most quotes come from 12+ month partners." },
    ],
    projects: [
      { name: "Founder, Atlas SaaS", tag: "B2B SaaS", result: "\"They feel like our team, not a vendor.\"" },
      { name: "Head of Product, Noor Retail", tag: "E-commerce", result: "\"Conversion gains paid for a year of work in a quarter.\"" },
      { name: "CTO, MediCare Connect", tag: "Healthcare", result: "\"Zero-downtime migration we didn't think was possible.\"" },
      { name: "COO, Fluent Pay", tag: "Fintech", result: "\"SOC 2 ready in 16 weeks — they earned the trust.\"" },
    ],
  },
  "ecommerce": {
    eyebrow: "Portfolio · Industries",
    title: "E-commerce",
    sub: "Storefronts, headless commerce and conversion engines.",
    metaDesc: "CoderLity e-commerce case studies — Shopify, headless commerce, CRO and growth for D2C and marketplace brands.",
    intro:
      "We design, build and grow e-commerce experiences for D2C brands, marketplaces and omnichannel retailers — on Shopify, custom headless stacks and everything in between.",
    blocks: [
      { icon: ShoppingCart, title: "Storefront craft", body: "Editorial PDPs, fast PLPs and frictionless checkout." },
      { icon: TrendingUp, title: "CRO playbooks", body: "Cart, checkout, search and PDP optimization at scale." },
      { icon: Workflow, title: "Ops integrations", body: "OMS, WMS, ERP and 3PL wiring that holds up at peak." },
      { icon: Zap, title: "Performance", body: "Sub-second LCP on PDP/PLP across mobile networks." },
    ],
    projects: [
      { name: "Noor Retail", tag: "Shopify Plus", result: "3.4× revenue in 12 months" },
      { name: "Atelier Maison", tag: "Headless commerce", result: "+58% mobile conversion" },
      { name: "Souq Bazaar", tag: "Marketplace", result: "Multi-vendor launch in 10 weeks" },
      { name: "Verde D2C", tag: "Subscription", result: "−42% churn after lifecycle rebuild" },
    ],
  },
  "healthcare": {
    eyebrow: "Portfolio · Industries",
    title: "Healthcare",
    sub: "EHR, telehealth and patient platforms — built for compliance.",
    metaDesc: "CoderLity healthcare case studies — EHR, telehealth, patient platforms and HIPAA / GDPR-grade engineering.",
    intro:
      "Healthcare doesn't forgive shortcuts. We build EHR integrations, telehealth platforms and patient apps with HIPAA, GDPR and regional compliance baked in from day one.",
    blocks: [
      { icon: Stethoscope, title: "Clinical workflows", body: "EHR, scheduling, e-prescribing and care plans." },
      { icon: ShieldCheck, title: "Compliance-first", body: "HIPAA, GDPR, NPHIES and regional standards." },
      { icon: Users, title: "Patient experience", body: "Accessible, multilingual patient apps that get used." },
      { icon: Workflow, title: "Integrations", body: "HL7, FHIR, lab and insurance gateways." },
    ],
    projects: [
      { name: "MediCare Connect", tag: "Telehealth", result: "120k records migrated, zero downtime" },
      { name: "ClinicOS", tag: "Practice management", result: "Multi-branch rollout to 24 clinics" },
      { name: "RxLink", tag: "E-prescribing", result: "Integrated with 6 national pharmacies" },
      { name: "Wellness Track", tag: "Patient app", result: "4.8★ across 12k reviews" },
    ],
  },
  "fintech": {
    eyebrow: "Portfolio · Industries",
    title: "Fintech",
    sub: "Payments, KYC and lending platforms — secure by design.",
    metaDesc: "CoderLity fintech case studies — payments, KYC/AML, lending and SOC 2 / PCI-grade engineering.",
    intro:
      "From digital wallets to lending engines, we build fintech products with KYC/AML, PCI and SOC 2 controls integrated end-to-end — not bolted on at the end.",
    blocks: [
      { icon: Banknote, title: "Payments + wallets", body: "Card, wallet, bank rails and FX flows." },
      { icon: ShieldCheck, title: "KYC / AML", body: "Identity, sanctions, PEP and risk scoring." },
      { icon: TrendingUp, title: "Lending engines", body: "Underwriting, decisioning and servicing." },
      { icon: Workflow, title: "Compliance", body: "SOC 2, PCI DSS, regional regulator audits." },
    ],
    projects: [
      { name: "Fluent Pay", tag: "Wallet + cards", result: "SOC 2 + launch in 16 weeks" },
      { name: "Lendly", tag: "BNPL", result: "−54% KYC drop-off" },
      { name: "Treasura", tag: "Treasury SaaS", result: "$1.2B AUC onboarded in year one" },
      { name: "Coinrail", tag: "Crypto on/off ramp", result: "MiCA-aligned launch across EU" },
    ],
  },
};

export const Route = createFileRoute("/portfolio/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/portfolio/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — Portfolio | CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — Portfolio | CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PortfolioChild,
});

function PortfolioChild() {
  const { item } = Route.useLoaderData();
  return (
    <PageShell>
      <PageHero eyebrow={item.eyebrow} title={item.title} sub={item.sub} />
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <p className="text-lg leading-relaxed text-muted-foreground">{item.intro}</p>
        </div>
      </section>
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {item.blocks.map((b: Block) => (
            <div key={b.title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                <b.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight">Selected work</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {item.projects.map((p: Project) => (
              <div key={p.name} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{p.tag}</div>
                <div className="mt-2 text-lg font-semibold">{p.name}</div>
                <div className="mt-3 text-sm text-gradient font-semibold">{p.result}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CTABanner />
    </PageShell>
  );
}
